import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import AtriumApi, { getApiErrorMessage } from "api";
import { formatTime } from "utils/format";
import Avatar from "components/avatar";
import Badge from "components/badge/Badge";
import Card from "components/card";
import Skeleton from "components/skeleton/Skeleton";
import { ErrorState } from "components/states";
import { PageContainer, PageHeader } from "components/page";

const CheckOut = () => {
  const [entries, setEntries] = useState([]);
  const [guardians, setGuardians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [queryParams] = useSearchParams();
  const code = queryParams.get("code");

  const loadCheckIn = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    AtriumApi.get("/v1/check-in", { params: { code: code } })
      .then((resp) => {
        setGuardians([resp.data.user, ...resp.data.guardians]);
        setEntries(resp.data.entries);
      })
      .catch((error) => setLoadError(error))
      .finally(() => setLoading(false));
  }, [code]);

  useEffect(() => {
    loadCheckIn();
  }, [loadCheckIn]);

  if (!code) throw new Error("not-found");

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-24 w-full rounded-card" />
          <Skeleton className="h-16 w-full rounded-card" />
          <Skeleton className="h-72 w-full rounded-card" />
        </div>
      );
    }

    if (loadError) {
      return (
        <ErrorState
          message={getApiErrorMessage(loadError, "We couldn’t load this check-in.")}
          onRetry={loadCheckIn}
        />
      );
    }

    return (
      <div className="flex flex-col gap-2">
        <ul className="flex flex-col gap-2">
          {entries.map(({ kid, room, ...entry }, i) => (
            <li key={"kid" + i}>
              <Card padding="sm">
                <div className="flex items-center gap-4">
                  <Avatar user={kid} size="lg" />
                  <div className="min-w-0 flex-1">
                    <p className="font-bold">
                      {kid.firstName} {kid.lastName}
                    </p>
                    <div className="flex flex-row items-end gap-2">
                      <div className="flex-1">
                        <p className="text-sm text-fg-muted">
                          {new Date().getFullYear() - new Date(kid.dob).getFullYear()}{" "}
                          years
                        </p>
                        <p className="mt-3 text-sm text-fg-muted">{room?.name}</p>
                      </div>
                      <div className="mt-3 text-right text-sm font-medium">
                        {entry.checkinAt ? (
                          <dl className="flex flex-col items-end gap-1">
                            <div>
                              <dt className="sr-only">Checked in</dt>
                              <dd className="border-b-2 border-line">{formatTime(entry.checkinAt)}</dd>
                            </div>
                            <div>
                              <dt className="sr-only">Checked out</dt>
                              <dd>
                                {entry.checkoutAt ? (
                                  formatTime(entry.checkoutAt)
                                ) : (
                                  <Badge tone="success">On-site</Badge>
                                )}
                              </dd>
                            </div>
                          </dl>
                        ) : (
                          <Badge tone="neutral">Processing</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </li>
          ))}
        </ul>

        <div className="flex flex-row gap-2">
          {guardians.map((user) => (
            <Card padding="sm" className="flex-1" key={`guardian${user.id}`}>
              <p className="text-sm text-fg-muted">Parent</p>
              <p className="text-base font-medium">
                {user.firstName} {user.lastName}
              </p>
            </Card>
          ))}
        </div>

        <Card padding="lg" className="items-center text-fg">
          <QRCodeSVG value={code} size={240} bgColor="transparent" fgColor="currentColor" />
        </Card>
      </div>
    );
  };

  return (
    <PageContainer>
      <PageHeader
        title="Check-out QR Code"
        description="Show this QR code to a staff member to check out."
      />
      {renderContent()}
    </PageContainer>
  );
};

export default CheckOut;
